import React, { useEffect, useState } from 'react';
import { Nav, Button, Badge } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaHome, 
  FaUsers, 
  FaCreditCard, 
  FaTicketAlt, 
  FaSignOutAlt, 
  FaChevronLeft, 
  FaChevronRight,
  FaChartBar,
  FaBell,
  FaFileInvoice,
  FaCog,
  FaCalendarAlt,
  FaEnvelope,
  FaLandmark,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import { adminSupportService } from '../../services/adminSupportService';

const PENDING_POLL_MS = 30000;

const AdminSidebar = ({ isCollapsed, toggleSidebar, onLogout }) => {
  const location = useLocation();
  const [pendingTickets, setPendingTickets] = useState(0);

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/admin-dashboard' || location.pathname === '/admin-dashboard/';
    }
    return location.pathname === `/admin-dashboard${path}`;
  };

  useEffect(() => {
    let cancelled = false;

    const loadPendingCount = async () => {
      try {
        const response = await adminSupportService.getPendingCount();
        if (!cancelled) {
          setPendingTickets(Number(response?.pending) || 0);
        }
      } catch (error) {
        console.error('Failed to load pending support ticket count:', error);
      }
    };

    loadPendingCount();
    const intervalId = setInterval(loadPendingCount, PENDING_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [location.pathname]);

  const pendingBadge =
    pendingTickets > 0 ? (
      <Badge
        bg="danger"
        pill
        className="sidebar-pending-badge"
        title={`${pendingTickets} pending support ticket${pendingTickets === 1 ? "" : "s"}`}
      >
        {pendingTickets > 99 ? "99+" : pendingTickets}
      </Badge>
    ) : null;

  return (
    <div className={`sidebar bg-dark text-white h-100 p-0 ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-4">
          {!isCollapsed && <h3 className="text-center mb-0">Admin Panel</h3>}
          <Button 
            variant="link" 
            className="text-white p-0 toggle-btn"
            onClick={toggleSidebar}
          >
            {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </Button>
        </div>
        <Nav className="flex-column">
          <Nav.Link 
            as={Link}
            to="/admin-dashboard"
            className={`text-white mb-2 ${isActive('/') ? 'active' : ''}`}
            title="Dashboard"
          >
            <FaHome className="me-2" /> {!isCollapsed && 'Dashboard'}
          </Nav.Link>
          <Nav.Link 
            as={Link}
            to="/admin-dashboard/users"
            className={`text-white mb-2 ${isActive('/users') ? 'active' : ''}`}
            title="Users List"
          >
            <FaUsers className="me-2" /> {!isCollapsed && 'User Management'}
          </Nav.Link>

          <Nav.Link 
            as={Link}
            to="/admin-dashboard/api-logs"
            className={`text-white mb-2 ${isActive('/api-logs') ? 'active' : ''}`}
            title="API Logs"
          >
            <FaChartBar className="me-2" /> {!isCollapsed && 'API Logs'}
          </Nav.Link>
          <Nav.Link
            as={Link}
            to="/admin-dashboard/email-logs"
            className={`text-white mb-2 ${isActive('/email-logs') ? 'active' : ''}`}
            title="Email Logs"
          >
            <FaEnvelope className="me-2" /> {!isCollapsed && 'Email Logs'}
          </Nav.Link>
          <Nav.Link 
            as={Link}
            to="/admin-dashboard/api-settings"
            className={`text-white mb-2 ${isActive('/api-settings') ? 'active' : ''}`}
            title="API Settings"
          >
            <FaCog className="me-2" /> {!isCollapsed && 'API Settings'}
          </Nav.Link>
          <Nav.Link
            as={Link}
            to="/admin-dashboard/forward-premiums"
            className={`text-white mb-2 ${isActive('/forward-premiums') ? 'active' : ''}`}
            title="Forward Premiums"
          >
            <FaCalendarAlt className="me-2" /> {!isCollapsed && 'Forward Premiums'}
          </Nav.Link>
          <Nav.Link
            as={Link}
            to="/admin-dashboard/rbi-reference-rates"
            className={`text-white mb-2 ${isActive('/rbi-reference-rates') ? 'active' : ''}`}
            title="RBI Reference Rates"
          >
            <FaLandmark className="me-2" /> {!isCollapsed && 'RBI Reference Rates'}
          </Nav.Link>
          <Nav.Link
            as={Link}
            to="/admin-dashboard/indian-customs-rates"
            className={`text-white mb-2 ${isActive('/indian-customs-rates') ? 'active' : ''}`}
            title="Indian Customs Rates"
          >
            <FaFileInvoiceDollar className="me-2" /> {!isCollapsed && 'Indian Customs Rates'}
          </Nav.Link>
          <Nav.Link 
            as={Link}
            to="/admin-dashboard/subscriptions"
            className={`text-white mb-2 ${isActive('/subscriptions') ? 'active' : ''}`}
            title="Subscriptions"
          >
            <FaCreditCard className="me-2" /> {!isCollapsed && 'Subscriptions'}
          </Nav.Link>
          <Nav.Link 
            as={Link}
            to="/admin-dashboard/invoice"
            className={`text-white mb-2 ${isActive('/invoice') ? 'active' : ''}`}
            title="Invoice"
          >
            <FaFileInvoice className="me-2" /> {!isCollapsed && 'Invoice'}
          </Nav.Link>
          <Nav.Link 
            as={Link}
            to="/admin-dashboard/notifications"
            className={`text-white mb-2 ${isActive('/notifications') ? 'active' : ''}`}
            title="Notifications"
          >
            <FaBell className="me-2" /> {!isCollapsed && 'Notifications'}
          </Nav.Link>
          {/* <Nav.Link 
            as={Link}
            to="/admin-dashboard/blog-management"
            className={`text-white mb-2 ${isActive('/blog-management') ? 'active' : ''}`}
            title="Blog Management"
          >
            <FaBlog className="me-2" /> {!isCollapsed && 'Blog Management'}
          </Nav.Link> */}
          <Nav.Link 
            as={Link}
            to="/admin-dashboard/support-tickets"
            className={`text-white mb-2 ${isActive('/support-tickets') ? 'active' : ''}`}
            title={pendingTickets > 0 ? `Support Tickets (${pendingTickets} pending)` : "Support Tickets"}
          >
            <FaTicketAlt className="me-2" />
            {!isCollapsed && "Support Tickets"}
            {pendingBadge}
          </Nav.Link>
          <Nav.Link 
            className="text-white mb-2"
            onClick={onLogout}
            title="Logout"
          >
            <FaSignOutAlt className="me-2" /> {!isCollapsed && 'Logout'}
          </Nav.Link>
        </Nav>
      </div>
    </div>
  );
};

export default AdminSidebar; 